import { memo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { AuraLayout, Layout, AddCardIcon } from "lola-framework-ui-test";

import { APP_ROUTES, flavorClassName } from "../constants";
import { detectCardBrand } from "../constants/card-brands";
import { FlowHeroMark } from "../components/FlowHeroMark";
import axios from "axios";
import { tokenizeCard, addCardPaymentMethod, getWidgetApiError, isDeadLinkError } from "../services/api";
import { isVgsConfigUnavailable } from "../utils/tokenization-config";
import { paymentType } from "../interfaces/payment";
import type { AddCardFullState } from "./AddCardAddressPage";
import { useClaimLink } from "../hooks/useClaimLink";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { getEvents, safeCapture } from "../analytics";

let isRegistrationInFlight = false;

const MIN_PROCESSING_VISIBLE_MS = 2000;

const holdMinimumVisible = (visibleSince: number) => {
  const remaining = MIN_PROCESSING_VISIBLE_MS - (Date.now() - visibleSince);
  if (remaining <= 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, remaining);
  });
};

export const AddCardProcessingPage = memo(() => {
  useClaimLink()
  const { t } = useTranslation();
  const {
    theme,
    cardState,
    setCardState,
    setPaymentState,
    cardList,
    setCardList,
  } = useStore();
  const { session } = useSessionStore();
  const navigate = useNavigate();
  const { redirectTo } = useRouteWithToken()
  const location = useLocation();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors } = colorConfig;

  const formData = location.state as AddCardFullState | undefined;
  const visibleSinceRef = useRef(Date.now());

  useEffect(() => {
    safeCapture(getEvents().ADD_CARD_PROCESSING_VIEW);
  }, []);

  useEffect(() => {
    if (!formData?.card || !formData?.address) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
      return;
    }

    if (isRegistrationInFlight) return;
    isRegistrationInFlight = true;

    const { card, address } = formData;

    const registerCard = async () => {
      try {
        const linkId = session.data?.linkId;
        const userStatus = session.data?.userStatus;
        const canSend = !userStatus?.blocked && !userStatus?.suspended;
        if (!canSend) {
          redirectTo(APP_ROUTES.TRANSACTIONS_PAGE, { replace: true });
          return;
        }
        if (!linkId) throw new Error("No linkId found in session");

        const cleanedNumber = card.cardNumber.replace(/\s/g, "");
        const brand = (detectCardBrand(cleanedNumber) || "OTHER").toUpperCase();
        const last4 = cleanedNumber.slice(-4);
        const [expiryMonth, expiryYearShort] = card.expiry.split("/");
        const expiryYear = `20${expiryYearShort}`;

        const tokenized = await tokenizeCard(cleanedNumber, card.cvv);

        const backendCard = await addCardPaymentMethod(linkId, {
          tokenizedCardNumber: tokenized.numberToken,
          tokenizedCvv: tokenized.cvvToken,
          last4,
          brand,
          expiryMonth,
          expiryYear,
          cardholderName: card.cardHolderName.trim(),
          billingAddress: {
            street1: address.street1.trim(),
            street2: address.street2.trim() || undefined,
            city: address.city.trim(),
            state: address.state,
            zipCode: address.zipCode.trim(),
            country: address.country || "US",
          },
        });

        const displayName = `${brand}-${last4}`;
        const cardData = {
          cardId: backendCard.paymentMethodId,
          name: displayName,
          brand,
          last4,
          accountNumber: tokenized.numberToken,
          expirationDate: card.expiry,
          fullName: card.cardHolderName.trim(),
          cvv: tokenized.cvvToken,
        };

        setCardState({ ...cardState, ...cardData });
        setPaymentState({
          payment: {
            cardId: backendCard.paymentMethodId,
            type: paymentType.CARD,
            brand: displayName,
          },
        });

        const cardExists = cardList.find(
          (c) => c.cardId === backendCard.paymentMethodId
        );
        if (!cardExists) setCardList([...cardList, cardData]);

        await holdMinimumVisible(visibleSinceRef.current);
        redirectTo(APP_ROUTES.TRANSFER_PAGE, { replace: true });
      } catch (error) {
        console.error("Failed to tokenize / register card:", error);

        // Link expired / revoked: the dead-link handler already routed to the
        // screen that explains why the link stopped working.
        if (isDeadLinkError(error)) {
          return;
        }

        const apiCode = getWidgetApiError(error)?.code;
        if (apiCode === "USER_BLOCKED" || apiCode === "USER_SUSPENDED") {
          navigate(APP_ROUTES.ERROR_PAGE, {
            replace: true,
            state: {
              title: t("error.transfersDisabledTitle"),
              subTitle: null,
              message: t("error.transfersDisabledMessage"),
              btnText: t("common.needHelp"),
              isOut: false,
              pageUrl: APP_ROUTES.SUPPORT_PAGE,
            },
          });
          return;
        }

        const isRateLimited =
          axios.isAxiosError(error) && error.response?.status === 429;

        navigate(APP_ROUTES.ERROR_PAGE, {
          replace: true,
          state: isRateLimited
            ? {
                title: t("error.rateLimitTitle", {
                  defaultValue: "Too Many Attempts",
                }),
                subTitle: t("error.rateLimitSubtitle", {
                  defaultValue: "Please slow down",
                }),
                message: t("error.rateLimitMessage"),
                btnText: t("card.tryAgain", { defaultValue: "Try again" }),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              }
            : {
                title: t("card.registrationFailedTitle", {
                  defaultValue: "Card Registration Failed",
                }),
                subTitle: t("card.registrationFailedSubtitle", {
                  defaultValue: "Could not add your card",
                }),
                message: isVgsConfigUnavailable(error)
                  ? t("vgs.configUnavailable")
                  : apiCode === "PAYMENT_METHOD_ALREADY_REGISTERED"
                    ? t("card.alreadyRegistered")
                    : t("card.registrationFailed"),
                btnText: t("card.tryAgain", { defaultValue: "Try again" }),
                isOut: false,
                pageUrl: APP_ROUTES.ADD_CARD_PAGE,
                returnTo: APP_ROUTES.TRANSFER_PAGE,
              },
        });
      } finally {
        isRegistrationInFlight = false;
      }
    };

    registerCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout
      colorConfig={colorConfig}
      className={`white-view-background ${flavorClassName}`}
    >
      <Layout.Content className="loading-section sending-money-section">
        <section className="sending-money-container">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundBtn}
          >
            <AddCardIcon
              size={60}
              colors={[textColorBtn, textColorBtn]}
              className="flavor-c-icon"
            />
          </FlowHeroMark>
          <section className="sending-caption">
            <h1 className="foreground highlight text-centered">
              {t("card.addingCard", { defaultValue: "Adding card" })}
              <span className="span-block lola-title-h1 h1">
                {t("card.pleaseWait", {
                  defaultValue: "Please wait...",
                })}
              </span>
            </h1>
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});